import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Gavel, Upload, TrendingUp, TrendingDown, EyeOff, Lock, Package,
  CreditCard, ArrowLeft, ArrowRight, Check, Image, Coins, Sparkles
} from 'lucide-react';
import uploadService from '../../../services/uploadService';
import ImageCropperModal from '../product/ImageCropperModal';

const AUCTION_TYPES = [
  { id: 'english', name: 'English Auction', icon: TrendingUp, desc: 'Ascending bids, highest wins', color: 'text-green-400' },
  { id: 'dutch', name: 'Dutch Auction', icon: TrendingDown, desc: 'Price drops, first bid wins', color: 'text-orange-400' },
  { id: 'silent', name: 'Silent Auction', icon: EyeOff, desc: 'Hidden bids, revealed at end', color: 'text-blue-400' },
  { id: 'reserve', name: 'Reserve Auction', icon: Lock, desc: 'Hidden minimum price', color: 'text-purple-400' },
  { id: 'multi_item', name: 'Multi-Item', icon: Package, desc: 'Multiple items, top N win', color: 'text-pink-400' },
];

const ITEM_CATEGORIES = [
  { id: 'service', name: 'Service' },
  { id: 'physical', name: 'Physical Product' },
  { id: 'digital', name: 'Digital Product' },
  { id: 'experience', name: 'Experience' },
  { id: 'nft', name: 'NFT' },
];

const STEPS = [
  { id: 'type', title: 'Auction Type' },
  { id: 'item', title: 'Item Details' },
  { id: 'pricing', title: 'Pricing' },
  { id: 'timing', title: 'Timing' },
  { id: 'payment', title: 'Payment' },
  { id: 'rewards', title: 'Rewards' },
  { id: 'preview', title: 'Preview' },
];

// Custom Switch Component
const Switch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Select Component
const Select = ({ value, onChange, options, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.id === value || opt.value === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.name || selectedOption?.label || 'Select...'}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.id || option.value}
              onClick={() => {
                onChange(option.id || option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                (option.id || option.value) === value ? 'text-purple-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.name || option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AuctionModal({
  isOpen,
  onClose,
  onSave,
  auction = null,
  folders = [],
  currentFolder = null,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Crop queue mirrors the Product Showcase flow — files picked from disk
  // open in the cropper one at a time before being uploaded so all auction
  // item images share the same 4:5 aspect.
  const [cropQueue, setCropQueue] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    auction_type: 'english',
    folder_id: currentFolder?.id || '',
    item: {
      name: '',
      description: '',
      images: [],
      category: 'service',
      estimatedValue: null,
    },
    starting_price: 100,
    reserve_price: null,
    bid_increment: 10,
    increment_type: 'fixed',
    buy_now_price: null,
    duration: 15,
    extend_on_bid: true,
    extension_time: 30,
    extension_threshold: 60,
    quantity: 1,
    price_drop_interval: 30,
    price_drop_amount: 50,
    require_deposit: false,
    deposit_amount: null,
    allow_proxy_bids: false,
    payment_methods: ['card'],
    token_discount_enabled: false,
    token_discount_percentage: 10,
    participation_reward: 10,
    bidder_reward: 25,
    winner_bonus: 100,
    token_holder_multiplier: 1,
  });

  useEffect(() => {
    if (auction) {
      setFormData({ ...formData, ...auction });
    } else {
      setFormData({
        name: '',
        auction_type: 'english',
        folder_id: currentFolder?.id || '',
        item: {
          name: '',
          description: '',
          images: [],
          category: 'service',
          estimatedValue: null,
        },
        starting_price: 100,
        reserve_price: null,
        bid_increment: 10,
        increment_type: 'fixed',
        buy_now_price: null,
        duration: 15,
        extend_on_bid: true,
        extension_time: 30,
        extension_threshold: 60,
        quantity: 1,
        price_drop_interval: 30,
        price_drop_amount: 50,
        require_deposit: false,
        deposit_amount: null,
        allow_proxy_bids: false,
        payment_methods: ['card'],
        token_discount_enabled: false,
        token_discount_percentage: 10,
        participation_reward: 10,
        bidder_reward: 25,
        winner_bonus: 100,
        token_holder_multiplier: 1,
      });
      setCurrentStep(0);
    }
  }, [auction, isOpen, currentFolder]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateItemField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      item: { ...prev.item, [field]: value }
    }));
  };

  // File picker → crop queue. We do NOT upload immediately; each file is
  // opened in the cropper modal first so the cropped 4:5 Blob is what gets
  // sent. Mirrors the Product Showcase flow.
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const accepted = files
      .slice(0, 5)
      .filter((f) => f.size <= 5 * 1024 * 1024);
    if (accepted.length === 0) return;
    setCropQueue(accepted);
  };

  // Upload a single cropped Blob and append the resulting URL to item.images.
  const uploadCroppedBlob = async (blob, originalFile) => {
    setIsUploading(true);
    try {
      const baseName = (originalFile?.name || 'image').replace(/\.[^.]+$/, '');
      const file =
        blob instanceof File
          ? blob
          : new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
      const result = await uploadService.uploadFile(file);
      const url = result?.url;
      if (!url) return;
      setFormData((prev) => ({
        ...prev,
        item: {
          ...prev.item,
          images: [...(prev.item?.images || []), url].slice(0, 5),
        },
      }));
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropConfirm = async (blob, originalFile) => {
    await uploadCroppedBlob(blob, originalFile);
    setCropQueue((prev) => prev.slice(1));
  };

  const handleCropCancel = () => {
    setCropQueue((prev) => prev.slice(1));
  };

  const removeImage = (index) => {
    updateItemField('images', formData.item.images.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!formData.name || !formData.item.name) return;

    setIsSaving(true);
    try {
      await onSave?.({
        ...formData,
        name: formData.name || formData.item.name,
      });
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.auction_type;
      case 1: return formData.item.name && formData.item.category;
      case 2: return formData.starting_price > 0 && formData.bid_increment > 0;
      case 3: return formData.duration > 0;
      default: return true;
    }
  };

  const togglePaymentMethod = (method) => {
    const methods = formData.payment_methods.includes(method)
      ? formData.payment_methods.filter(m => m !== method)
      : [...formData.payment_methods, method];
    updateField('payment_methods', methods);
  };

  if (!isOpen) return null;

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#0a0a1a] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-white/10"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {auction ? 'Edit Auction' : 'Create Auction'}
              </h2>
              <p className="text-xs text-gray-400">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-4 py-2 border-b border-white/10">
          <div className="flex gap-1">
            {STEPS.map((step, idx) => (
              <div
                key={step.id}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  idx <= currentStep ? 'bg-purple-500' : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Read-only banner */}
          {readOnly && (
            <div className="flex items-start gap-3 mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              <p className="text-sm text-purple-200">
                This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
              </p>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* Step 0: Auction Type */}
            {currentStep === 0 && (
              <motion.div
                key="type"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Select Auction Type</h3>
                <div className="grid gap-3">
                  {AUCTION_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => updateField('auction_type', type.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                        formData.auction_type === type.id
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-white/10 hover:border-white/20 bg-white/5'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center ${type.color}`}>
                        <type.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-white">{type.name}</div>
                        <div className="text-sm text-gray-400">{type.desc}</div>
                      </div>
                      {formData.auction_type === type.id && (
                        <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 1: Item Details */}
            {currentStep === 1 && (
              <motion.div
                key="item"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Item Details</h3>

                {!skipFolderSelect && folders.length > 0 && (
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Folder</label>
                    <Select
                      value={formData.folder_id}
                      onChange={(v) => updateField('folder_id', v)}
                      options={folders.map(f => ({ id: f.id, name: f.name }))}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Auction Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    disabled={readOnly}
                    placeholder="VIP Coaching Package Auction"
                    className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Item Name *</label>
                  <input
                    value={formData.item.name}
                    onChange={(e) => updateItemField('name', e.target.value)}
                    disabled={readOnly}
                    placeholder="6-Month VIP Coaching Package"
                    className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Description</label>
                  <textarea
                    value={formData.item.description}
                    onChange={(e) => updateItemField('description', e.target.value)}
                    disabled={readOnly}
                    placeholder="Describe what the winner will receive..."
                    className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white min-h-[100px] resize-none focus:outline-none ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Category *</label>
                  <Select
                    value={formData.item.category}
                    onChange={(v) => updateItemField('category', v)}
                    options={ITEM_CATEGORIES}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Item Images</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {formData.item.images.map((img, idx) => (
                      <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeImage(idx)}
                          className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                        >
                          <X className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    ))}
                    {!readOnly && formData.item.images.length < 5 && (
                      <label className="w-20 h-20 rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-white/40 transition-colors">
                        {isUploading ? (
                          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload className="w-5 h-5 text-gray-400" />
                        )}
                        <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">Upload up to 5 images</p>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Estimated Value (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.item.estimatedValue || ''}
                      onChange={(e) => updateItemField('estimatedValue', e.target.value ? Number(e.target.value) : null)}
                      placeholder="5000"
                      className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Pricing */}
            {currentStep === 2 && (
              <motion.div
                key="pricing"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Pricing & Bidding Rules</h3>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Starting Price *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.starting_price}
                      onChange={(e) => updateField('starting_price', Number(e.target.value))}
                      className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Bid Increment</label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Select
                        value={formData.increment_type}
                        onChange={(v) => updateField('increment_type', v)}
                        options={[
                          { id: 'fixed', name: 'Fixed Amount' },
                          { id: 'percentage', name: 'Percentage' },
                        ]}
                      />
                    </div>
                    <div className="flex-1 relative">
                      {formData.increment_type === 'fixed' && (
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      )}
                      <input
                        type="number"
                        value={formData.bid_increment}
                        onChange={(e) => updateField('bid_increment', Number(e.target.value))}
                        className={`w-full pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none ${formData.increment_type === 'fixed' ? 'pl-7' : 'pl-3'}`}
                      />
                      {formData.increment_type === 'percentage' && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Reserve Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.reserve_price || ''}
                      onChange={(e) => updateField('reserve_price', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Hidden minimum price"
                      className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Bidders won't see this price</p>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Buy Now Price (optional)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={formData.buy_now_price || ''}
                      onChange={(e) => updateField('buy_now_price', e.target.value ? Number(e.target.value) : null)}
                      placeholder="Instant purchase price"
                      className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                  </div>
                </div>

                {formData.auction_type === 'multi_item' && (
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Quantity Available</label>
                    <input
                      type="number"
                      min={2}
                      value={formData.quantity}
                      onChange={(e) => updateField('quantity', Number(e.target.value))}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Top {formData.quantity} bids will win</p>
                  </div>
                )}

                <div className="space-y-3 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-white">Allow Proxy Bidding</span>
                      <p className="text-xs text-gray-500">Auto-bid up to max amount</p>
                    </div>
                    <Switch
                      checked={formData.allow_proxy_bids}
                      onChange={(v) => updateField('allow_proxy_bids', v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-white">Require Deposit to Bid</span>
                      <p className="text-xs text-gray-500">Refundable if not winner</p>
                    </div>
                    <Switch
                      checked={formData.require_deposit}
                      onChange={(v) => updateField('require_deposit', v)}
                    />
                  </div>

                  {formData.require_deposit && (
                    <div className="pl-4">
                      <label className="text-sm text-gray-400 mb-1 block">Deposit Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={formData.deposit_amount || ''}
                          onChange={(e) => updateField('deposit_amount', Number(e.target.value))}
                          className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 3: Timing */}
            {currentStep === 3 && (
              <motion.div
                key="timing"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Auction Duration</h3>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Duration (minutes) *</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.duration}
                    onChange={(e) => updateField('duration', Number(e.target.value))}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                  />
                </div>

                {formData.auction_type === 'dutch' && (
                  <>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Price Drop Interval (seconds)</label>
                      <input
                        type="number"
                        value={formData.price_drop_interval}
                        onChange={(e) => updateField('price_drop_interval', Number(e.target.value))}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 mb-1 block">Price Drop Amount</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <input
                          type="number"
                          value={formData.price_drop_amount}
                          onChange={(e) => updateField('price_drop_amount', Number(e.target.value))}
                          className="w-full pl-7 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-white">Time Extensions</span>
                      <p className="text-xs text-gray-500">Extend when bid placed near end</p>
                    </div>
                    <Switch
                      checked={formData.extend_on_bid}
                      onChange={(v) => updateField('extend_on_bid', v)}
                    />
                  </div>

                  {formData.extend_on_bid && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">Add seconds</label>
                        <input
                          type="number"
                          value={formData.extension_time}
                          onChange={(e) => updateField('extension_time', Number(e.target.value))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 mb-1 block">When within (seconds)</label>
                        <input
                          type="number"
                          value={formData.extension_threshold}
                          onChange={(e) => updateField('extension_threshold', Number(e.target.value))}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500">
                  Time extensions prevent "sniping" and give others a chance to counter-bid
                </p>
              </motion.div>
            )}

            {/* Step 4: Payment */}
            {currentStep === 4 && (
              <motion.div
                key="payment"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Payment Methods</h3>

                <div className="space-y-3">
                  {[
                    { id: 'card', name: 'Credit/Debit Card', icon: CreditCard, disabled: false },
                    // Cryptocurrency is shown as "Coming Soon" — buyer-facing
                    // crypto payments aren't yet supported on the platform
                    // (only affiliate payouts use crypto today).
                    { id: 'crypto', name: 'Cryptocurrency', icon: Coins, disabled: true },
                    { id: 'rly_tokens', name: 'RLY Tokens', icon: Sparkles, disabled: false },
                  ].map((method) => {
                    const selected = formData.payment_methods.includes(method.id);
                    return (
                      <button
                        key={method.id}
                        type="button"
                        disabled={method.disabled}
                        onClick={() => !method.disabled && togglePaymentMethod(method.id)}
                        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                          method.disabled
                            ? 'border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed'
                            : selected
                              ? 'border-purple-500 bg-purple-500/10'
                              : 'border-white/10 hover:border-white/20 bg-white/5'
                        }`}
                      >
                        <method.icon className="w-5 h-5 text-gray-400" />
                        <span className="text-white">{method.name}</span>
                        {method.disabled ? (
                          <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-gray-400 uppercase tracking-wide">
                            Coming Soon
                          </span>
                        ) : selected ? (
                          <Check className="w-4 h-4 text-purple-400 ml-auto" />
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {/* Token Payment Discount */}
                <div className="p-5 rounded-xl border border-white/10 bg-white/5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white font-medium">Token Payment Discount</p>
                      <p className="text-xs text-gray-400 mt-0.5">Offer discount for token payments</p>
                    </div>
                    <Switch
                      checked={!!formData.token_discount_enabled}
                      onChange={(v) => updateField('token_discount_enabled', v)}
                    />
                  </div>

                  {formData.token_discount_enabled && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm text-gray-400">Discount Percentage</label>
                        <span className="text-white font-semibold">{formData.token_discount_percentage}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={50}
                        step={1}
                        value={formData.token_discount_percentage}
                        onChange={(e) => updateField('token_discount_percentage', Number(e.target.value))}
                        className="w-full accent-[#6a1fbf]"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 5: Token Rewards */}
            {currentStep === 5 && (
              <motion.div
                key="rewards"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-lg font-semibold text-white">Token Rewards</h3>
                  <p className="text-sm text-gray-400 mt-1">Reward participants with tokens for engagement</p>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Participation Reward</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={0}
                      value={formData.participation_reward}
                      onChange={(e) => updateField('participation_reward', Number(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">RLY just for watching</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Bidder Reward</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={0}
                      value={formData.bidder_reward}
                      onChange={(e) => updateField('bidder_reward', Number(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">RLY for placing any bid</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Winner Bonus</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={0}
                      value={formData.winner_bonus}
                      onChange={(e) => updateField('winner_bonus', Number(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">RLY extra for winning</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Token Holder Multiplier</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={1}
                      step={0.1}
                      value={formData.token_holder_multiplier}
                      onChange={(e) => updateField('token_holder_multiplier', Number(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none"
                    />
                    <span className="text-sm text-gray-400 whitespace-nowrap">x rewards for token holders</span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 6: Preview */}
            {currentStep === 6 && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h3 className="text-lg font-semibold text-white mb-4">Preview Your Auction</h3>

                <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-white/10">
                  <div className="flex gap-6">
                    {formData.item.images.length > 0 ? (
                      <img
                        src={formData.item.images[0]}
                        alt=""
                        className="w-32 h-32 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-32 h-32 rounded-xl bg-white/10 flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-500" />
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${AUCTION_TYPES.find(t => t.id === formData.auction_type)?.color} bg-white/10`}>
                          {AUCTION_TYPES.find(t => t.id === formData.auction_type)?.name}
                        </span>
                        <span className="text-xs text-gray-400">{ITEM_CATEGORIES.find(c => c.id === formData.item.category)?.name}</span>
                      </div>
                      <h4 className="text-xl font-bold text-white">{formData.item.name || 'Untitled Item'}</h4>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">{formData.item.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/10">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">${formData.starting_price}</div>
                      <div className="text-xs text-gray-500">Starting Price</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-400">${formData.bid_increment}</div>
                      <div className="text-xs text-gray-500">Min Increment</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-400">{formData.duration}m</div>
                      <div className="text-xs text-gray-500">Duration</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-400">{formData.winner_bonus}</div>
                      <div className="text-xs text-gray-500">Winner Bonus</div>
                    </div>
                  </div>

                  {formData.buy_now_price && (
                    <div className="mt-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                      <span className="text-yellow-400">Buy Now: ${formData.buy_now_price}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-between">
          <button
            onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-600 transition-colors"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : readOnly ? (onCopyToMyElements && (
            <button
              type="button"
              onClick={onCopyToMyElements}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copy to My Elements
            </button>
          )) : (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Gavel className="w-4 h-4" />
              )}
              {auction ? 'Save Changes' : 'Create Auction'}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <>
      {ReactDOM.createPortal(modalContent, document.body)}
      {/* Image cropper — opens whenever a file is queued. The cropper portals
          itself to document.body, so it stacks above the auction modal. */}
      {cropQueue.length > 0 && (
        <ImageCropperModal
          key={`${cropQueue[0].name}-${cropQueue[0].size}-${cropQueue[0].lastModified}`}
          file={cropQueue[0]}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}
    </>
  );
}
